import React, { useCallback, useEffect } from 'react';
import { LazyLoadListScrollTrigger } from './LazyLoadListTrigger';

type RenderProps = {
  shouldRenderIndex: (i: number) => boolean;
};
type RenderPropChildren = (props: RenderProps) => React.ReactNode;
export interface LazyLoadListProps {
  /**
   * The number of items to render at once when renderMore is called.
   * Defaults to 25
   */
  pageSize?: number;

  /**
   * The total number of items that will be rendered.
   */
  numOfItems: number;

  /**
   * If false, then lazy loading is disabled.
   */
  active?: boolean;

  /**
   * The number of items to render on the first pass. Defaults to pageSize.
   */
  initialPageSize?: number;

  children: RenderPropChildren;

  rootRef?: HTMLElement | null;

  /**
   * Callback invoked when number of items to render changes (including initial render).
   */
  onRenderPage?: (numItemsToRender: number) => void;
}

export const LazyLoadList: React.FunctionComponent<LazyLoadListProps> = ({
  children,
  pageSize = 25,
  numOfItems,
  active = true,
  initialPageSize: userSetInitialPageSize,
  rootRef,
  onRenderPage,
}) => {
  const initialPageSize = userSetInitialPageSize || pageSize;
  const [numItemsToRender, setNumItemsToRender] = React.useState(initialPageSize);

  // need to guarantee an initial call
  useEffect(() => {
    onRenderPage?.(numItemsToRender);
  }, [onRenderPage]);

  const renderMore = React.useCallback(() => {
    if (numOfItems > numItemsToRender) {
      const newNumItemsToRender = numItemsToRender + pageSize;
      setNumItemsToRender(newNumItemsToRender);
      onRenderPage?.(newNumItemsToRender);
    }
  }, [pageSize, numOfItems, numItemsToRender, onRenderPage]);

  const shouldRenderIndex = useCallback(
    (itemIndex: number) => {
      if (!active) return true;
      if (numItemsToRender == null) return false;
      if (numItemsToRender > itemIndex && active) {
        return true;
      }
      return false;
    },
    [active, numItemsToRender],
  );

  /**
   * Explanation of `key={numItemsToRender}` on LazyLoadListScrollTrigger:
   * Intersection observer can get a bit wonky when it's used in a way that causes it to change location multiple
   * times like this one does. So, we force it to unmount and remount by adding a key based on the numItemsToRender
   */
  return (
    <>
      {children({ shouldRenderIndex })}
      <LazyLoadListScrollTrigger rootRef={rootRef} key={numItemsToRender} renderMore={renderMore} />
    </>
  );
};
